import React, { useState, useMemo, useRef, useCallback } from 'react';
import { format as formatSQL } from 'sql-formatter';
import { TabState, PaneNode } from '../../../../types/pane.types';
import { createPaneId, PaneId } from '../../../../types/id.types';
import { useProjectStore } from '../../../stores/projectStore';
import { usePaneLayout } from '../../../stores/hooks/usePaneLayout';
import { useConfirmDialogStore } from '../../../stores/confirmDialogStore';
import {
  createDataTableTab,
  createReportTab,
  createModelDetailTab
} from '../../../tab-system/TabRegistry';
import {
  LineageFilterSettings,
  LineageSelectionState,
  LineageNode,
  LineageNodeType
} from '../../../../types/lineage.types';
import {
  DerivedDataSet,
  DataSet,
  PrimaryDataSet,
  SQLQueryOperation,
  CrosstabOperation,
  FilterOperation,
  DatasetId
} from '../../../../types/dataset.types';
import { Report } from '../../../../types/report.types';
import { TrainedModel } from '../../../../types/model.types';
import { ModelId } from '../../../../types/id.types';
import { buildDependencyGraph } from '../../../utils/lineageGraphBuilder';
import {
  checkDatasetDependencies,
  checkReportDependencies,
  checkModelDependencies,
  generateDependencyWarningMessage,
  generateReportDependencyWarningMessage,
  generateModelDependencyWarningMessage
} from '../../../utils/dependencyChecker';
import LineageGraph from '../../visualization/LineageGraph';
import { LineageContextMenu } from '../../visualization/LineageContextMenu';
import { Resizer } from '../../Resizer';
import * as styles from './ProjectLineageTabContent.css.ts';
import logger from '../../../utils/logger';

interface ProjectLineageTabContentProps {
  tab: TabState;
}

// コンテキストメニューの状態
interface ContextMenuState {
  visible: boolean;
  x: number;
  y: number;
  nodeId: string | null;
  nodeType: LineageNodeType | null;
}

export const ProjectLineageTabContent: React.FC<ProjectLineageTabContentProps> = () => {
  const project = useProjectStore((state) => state.project);
  const removeDataset = useProjectStore((state) => state.removeDataset);
  const updateProject = useProjectStore((state) => state.updateProject);
  const layout = usePaneLayout();
  const workspaces = useMemo(() => project?.workspaces ?? [], [project?.workspaces]);
  const confirm = useConfirmDialogStore((state) => state.confirm);

  // Resizer用のref
  const graphViewRef = useRef<HTMLDivElement>(null);
  const detailsPanelRef = useRef<HTMLDivElement>(null);

  // フィルタ設定
  const [filterSettings, setFilterSettings] = useState<LineageFilterSettings>({
    showPrimaryDatasets: true,
    showDerivedDatasets: true,
    showReports: true,
    showTabs: true,
    showModels: true
  });
  
  
  // 選択状態
  const [selectionState, setSelectionState] = useState<LineageSelectionState>({
    selectedNodeId: null,
    highlightedNodeIds: new Set(),
    highlightedEdgeIds: new Set()
  });
  
  // 選択されたノードの詳細
  const [selectedNodeDetails, setSelectedNodeDetails] = useState<LineageNode | null>(null);

  // コンテキストメニューの状態
  const [contextMenu, setContextMenu] = useState<ContextMenuState>({
    visible: false,
    x: 0,
    y: 0,
    nodeId: null,
    nodeType: null
  });
  
  // 依存関係グラフを構築
  const dependencyGraph = useMemo(() => {
    if (!project) return { nodes: [], edges: [] };

    const allTabs: TabState[] = [];
    const collectTabs = (node: PaneNode): void => {
      if (!node) {
        logger.warn('ProjectLineage: Null node encountered');
        return;
      }
      
      
      if (node.type === 'container') {
        if (node.tabs && Array.isArray(node.tabs)) {
          allTabs.push(...node.tabs);
        }
      } else if (node.type === 'split') {
        collectTabs(node.leftChild);
        collectTabs(node.rightChild);
      }
    };
    
    // 全ワークスペースからタブを収集
    workspaces.forEach((workspace) => {
      
      if (workspace.layoutState && workspace.layoutState.rootPane) {
        collectTabs(workspace.layoutState.rootPane);
      }
    });
    
    // 現在のレイアウトからもタブを収集（保存されていない場合のため）
    if (layout && layout.layout && layout.layout.rootPane) {
      collectTabs(layout.layout.rootPane);
    }
    
    // 重複を削除
    const uniqueTabs = Array.from(new Map(allTabs.map(tab => [tab.id, tab])).values());
    
    logger.info('ProjectLineage: Tab collection complete', { 
      totalTabs: uniqueTabs.length,
      tabIds: uniqueTabs.map(t => t.id),
      workspacesProcessed: workspaces.length
    });
    
    return buildDependencyGraph(project, uniqueTabs);
  }, [project, layout, workspaces]);
  
  // フィルタリングされたグラフ
  const filteredGraph = useMemo(() => {
    const filteredNodes = dependencyGraph.nodes.filter(node => {
      switch (node.type) {
        case 'primary-dataset':
          return filterSettings.showPrimaryDatasets;
        case 'derived-dataset':
          return filterSettings.showDerivedDatasets;
        case 'report':
          return filterSettings.showReports;
        case 'tab':
          return filterSettings.showTabs;
        case 'model':
          return filterSettings.showModels;
        default:
          return true;
      }
    });
    
    const nodeIds = new Set(filteredNodes.map(n => n.id));
    const filteredEdges = dependencyGraph.edges.filter(edge => 
      nodeIds.has(edge.source) && nodeIds.has(edge.target)
    );
    
    return { nodes: filteredNodes, edges: filteredEdges };
  }, [dependencyGraph, filterSettings]);
  
  // ノード選択ハンドラ
  const handleNodeSelect = (nodeId: string | null) => {
    setSelectionState(prev => ({
      ...prev,
      selectedNodeId: nodeId
    }));

    if (nodeId) {
      const node = filteredGraph.nodes.find(n => n.id === nodeId);
      setSelectedNodeDetails(node || null);
    } else {
      setSelectedNodeDetails(null);
      // Detailsパネルを閉じたときにgraphViewのwidthをリセット
      if (graphViewRef.current) {
        graphViewRef.current.style.width = '';
        graphViewRef.current.style.flex = '1';
      }
    }
  };

  // タブ検索ヘルパー関数群
  const findTabByDatasetId = (rootPane: PaneNode, datasetId: string): { containerId: string; tab: TabState } | null => {
    const search = (pane: PaneNode): { containerId: string; tab: TabState } | null => {
      if (pane.type === 'container') {
        const tab = pane.tabs.find(t => t.metadata?.datasetId === datasetId);
        if (tab) return { containerId: pane.id, tab };
      } else if (pane.type === 'split') {
        return search(pane.leftChild) || search(pane.rightChild);
      }
      return null;
    };
    return search(rootPane);
  };

  const findTabByReportId = (rootPane: PaneNode, reportId: string): { containerId: string; tab: TabState } | null => {
    const search = (pane: PaneNode): { containerId: string; tab: TabState } | null => {
      if (pane.type === 'container') {
        const tab = pane.tabs.find(t => t.metadata?.reportId === reportId);
        if (tab) return { containerId: pane.id, tab };
      } else if (pane.type === 'split') {
        return search(pane.leftChild) || search(pane.rightChild);
      }
      return null;
    };
    return search(rootPane);
  };

  const findTabByModelId = (rootPane: PaneNode, modelId: string): { containerId: string; tab: TabState } | null => {
    const search = (pane: PaneNode): { containerId: string; tab: TabState } | null => {
      if (pane.type === 'container') {
        const tab = pane.tabs.find(t => t.metadata?.modelId === modelId);
        if (tab) return { containerId: pane.id, tab };
      } else if (pane.type === 'split') {
        return search(pane.leftChild) || search(pane.rightChild);
      }
      return null;
    };
    return search(rootPane);
  };

  const findTabById = (rootPane: PaneNode, tabId: string): { containerId: string; tab: TabState } | null => {
    const search = (pane: PaneNode): { containerId: string; tab: TabState } | null => {
      if (pane.type === 'container') {
        const foundTab = pane.tabs.find(t => t.id === tabId);
        if (foundTab) return { containerId: pane.id, tab: foundTab };
      } else if (pane.type === 'split') {
        return search(pane.leftChild) || search(pane.rightChild);
      }
      return null;
    };
    return search(rootPane);
  };

  // ノードタイプ別ハンドラー
  const handleDatasetDoubleClick = useCallback((dataset: DataSet, activeContainerId: string) => {
    const existingTab = findTabByDatasetId(layout.layout.rootPane, dataset.id);
    if (existingTab) {
      layout.activateTab(existingTab.containerId, existingTab.tab.id);
      logger.info('[ProjectLineage] Activated existing tab for dataset:', {
        datasetId: dataset.id, tabId: existingTab.tab.id
      });
    } else {
      const newTab = createDataTableTab(dataset.id, dataset.name);
      layout.addTab(activeContainerId, newTab, { activatePane: true });
      logger.info('[ProjectLineage] Created new tab for dataset:', {
        datasetId: dataset.id, tabId: newTab.id
      });
    }
  }, [layout]);

  const handleReportDoubleClick = useCallback((report: Report, activeContainerId: string) => {
    const existingTab = findTabByReportId(layout.layout.rootPane, report.id);
    if (existingTab) {
      layout.activateTab(existingTab.containerId, existingTab.tab.id);
      logger.info('[ProjectLineage] Activated existing tab for report:', {
        reportId: report.id, tabId: existingTab.tab.id
      });
    } else {
      const newTab = createReportTab(report.id, report.name);
      layout.addTab(activeContainerId, newTab, { activatePane: true });
      logger.info('[ProjectLineage] Created new tab for report:', {
        reportId: report.id, tabId: newTab.id
      });
    }
  }, [layout]);

  const handleModelDoubleClick = useCallback((model: TrainedModel, activeContainerId: string) => {
    const existingTab = findTabByModelId(layout.layout.rootPane, model.id);
    if (existingTab) {
      layout.activateTab(existingTab.containerId, existingTab.tab.id);
      logger.info('[ProjectLineage] Activated existing tab for model:', {
        modelId: model.id, tabId: existingTab.tab.id
      });
    } else {
      const newTab = createModelDetailTab(model.id, model.name);
      layout.addTab(activeContainerId, newTab, { activatePane: true });
      logger.info('[ProjectLineage] Created new tab for model:', {
        modelId: model.id, tabId: newTab.id
      });
    }
  }, [layout]);

  const handleTabDoubleClick = useCallback((tab: TabState) => {
    const existingTab = findTabById(layout.layout.rootPane, tab.id);
    if (existingTab) {
      layout.activateTab(existingTab.containerId, existingTab.tab.id);
      logger.info('[ProjectLineage] Activated tab:', { tabId: tab.id });
    } else {
      logger.warn('[ProjectLineage] Tab not found:', { tabId: tab.id });
    }
  }, [layout]);

  // 最初のコンテナを探すヘルパー関数
  const findFirstContainerIdStr = useCallback((node: PaneNode): string | null => {
    if (node.type === 'container') {
      return node.id;
    }
    if (node.type === 'split') {
      return findFirstContainerIdStr(node.leftChild) || findFirstContainerIdStr(node.rightChild);
    }
    return null;
  }, []);

  // ノードダブルクリックハンドラ
  const handleNodeDoubleClick = useCallback((nodeId: string) => {
    const node = filteredGraph.nodes.find(n => n.id === nodeId);
    if (!node) return;

    // アクティブコンテナを取得、なければ最初のコンテナをフォールバックとして使用
    let activeContainerId: PaneId | undefined = layout.getActiveContainer()?.id;
    if (!activeContainerId) {
      const fallbackIdStr = findFirstContainerIdStr(layout.layout.rootPane);
      if (fallbackIdStr) {
        activeContainerId = createPaneId(fallbackIdStr);
      }
      logger.warn('[ProjectLineage] No active container, using fallback:', { fallbackId: activeContainerId });
    }

    if (!activeContainerId) {
      logger.error('[ProjectLineage] No container found at all');
      return;
    }

    switch (node.type) {
      case 'primary-dataset':
      case 'derived-dataset':
        handleDatasetDoubleClick(node.data as DataSet, activeContainerId);
        break;
      case 'report':
        handleReportDoubleClick(node.data as Report, activeContainerId);
        break;
      case 'model':
        handleModelDoubleClick(node.data as TrainedModel, activeContainerId);
        break;
      case 'tab':
        handleTabDoubleClick(node.data as TabState);
        break;
      default:
        logger.warn('[ProjectLineage] Unknown node type:', { nodeType: node.type });
    }
  }, [filteredGraph.nodes, layout, findFirstContainerIdStr, handleDatasetDoubleClick, handleReportDoubleClick, handleModelDoubleClick, handleTabDoubleClick]);

  // コンテキストメニューハンドラ
  const handleNodeContextMenu = useCallback((nodeId: string, x: number, y: number) => {
    const node = filteredGraph.nodes.find(n => n.id === nodeId);
    if (!node) return;

    setContextMenu({
      visible: true,
      x,
      y,
      nodeId,
      nodeType: node.type
    });
  }, [filteredGraph.nodes]);

  const handleContextMenuClose = useCallback(() => {
    setContextMenu(prev => ({ ...prev, visible: false }));
  }, []);

  // コンテキストメニューのOpenアクション
  const handleContextMenuOpen = useCallback(() => {
    if (!contextMenu.nodeId) return;
    handleNodeDoubleClick(contextMenu.nodeId);
  }, [contextMenu.nodeId, handleNodeDoubleClick]);

  // データセット削除処理
  const handleDeleteDataset = useCallback(async (dataset: DataSet) => {
    if (!project) return;

    const dependencies = checkDatasetDependencies(project, dataset.id, layout.layout);

    if (dependencies.hasAnyDependencies) {
      const warningMessage = generateDependencyWarningMessage(dependencies, dataset.name);
      const confirmed = await confirm({
        title: 'Delete Dataset',
        message: `Are you sure you want to delete "${dataset.name}"?`,
        details: warningMessage,
        confirmLabel: 'Delete All',
        confirmStyle: 'danger'
      });

      if (!confirmed) return;

      // 依存するタブを閉じる
      for (const tab of dependencies.tabs) {
        const tabInfo = findTabById(layout.layout.rootPane, tab.id);
        if (tabInfo) {
          layout.closeTab(tabInfo.containerId, tab.id);
        }
      }

      // 依存するレポートを削除
      if (dependencies.reports.length > 0) {
        updateProject((proj) => ({
          ...proj,
          reports: proj.reports.filter(r => !dependencies.reports.find(dr => dr.id === r.id))
        }));
      }

      // 依存するモデルを削除
      if (dependencies.models.length > 0) {
        updateProject((proj) => {
          const newModels = { ...proj.models };
          dependencies.models.forEach(m => delete newModels[m.id as ModelId]);
          return { ...proj, models: newModels };
        });
      }

      // 依存するDerivedDatasetを削除
      for (const derived of dependencies.derivedDatasets) {
        removeDataset(derived.id as DatasetId);
      }
    }

    // データセットを削除
    removeDataset(dataset.id);
    logger.info('[ProjectLineage] Dataset deleted:', { datasetId: dataset.id, datasetName: dataset.name });
  }, [project, layout, confirm, updateProject, removeDataset]);

  // レポート削除処理
  const handleDeleteReport = useCallback(async (report: Report) => {
    if (!project) return;

    const dependencies = checkReportDependencies(report.id, layout.layout);

    if (dependencies.hasAnyDependencies) {
      const warningMessage = generateReportDependencyWarningMessage(dependencies, report.name);
      const confirmed = await confirm({
        title: 'Delete Report',
        message: `Are you sure you want to delete "${report.name}"?`,
        details: warningMessage,
        confirmLabel: 'Delete',
        confirmStyle: 'danger'
      });

      if (!confirmed) return;

      // 依存するタブを閉じる
      for (const tab of dependencies.tabs) {
        const tabInfo = findTabById(layout.layout.rootPane, tab.id);
        if (tabInfo) {
          layout.closeTab(tabInfo.containerId, tab.id);
        }
      }
    }

    // レポートを削除
    updateProject((proj) => ({
      ...proj,
      reports: proj.reports.filter(r => r.id !== report.id)
    }));
    logger.info('[ProjectLineage] Report deleted:', { reportId: report.id, reportName: report.name });
  }, [project, layout, confirm, updateProject]);

  // モデル削除処理
  const handleDeleteModel = useCallback(async (model: TrainedModel) => {
    if (!project) return;

    const dependencies = checkModelDependencies(project, model.id, layout.layout);

    if (dependencies.hasAnyDependencies) {
      const warningMessage = generateModelDependencyWarningMessage(dependencies, model.name);
      const confirmed = await confirm({
        title: 'Delete Model',
        message: `Are you sure you want to delete "${model.name}"?`,
        details: warningMessage,
        confirmLabel: 'Delete All',
        confirmStyle: 'danger'
      });

      if (!confirmed) return;

      // 依存するタブを閉じる
      for (const tab of dependencies.tabs) {
        const tabInfo = findTabById(layout.layout.rootPane, tab.id);
        if (tabInfo) {
          layout.closeTab(tabInfo.containerId, tab.id);
        }
      }

      // 依存するDerivedDatasetを削除
      for (const derived of dependencies.derivedDatasets) {
        removeDataset(derived.id as DatasetId);
      }
    }

    // モデルを削除
    updateProject((proj) => {
      const newModels = { ...proj.models };
      delete newModels[model.id];
      return { ...proj, models: newModels };
    });
    logger.info('[ProjectLineage] Model deleted:', { modelId: model.id, modelName: model.name });
  }, [project, layout, confirm, updateProject, removeDataset]);

  // タブを閉じる処理
  const handleCloseTab = useCallback((tab: TabState) => {
    const tabInfo = findTabById(layout.layout.rootPane, tab.id);
    if (tabInfo) {
      layout.closeTab(tabInfo.containerId, tab.id);
      logger.info('[ProjectLineage] Tab closed:', { tabId: tab.id, tabTitle: tab.title });
    }
  }, [layout]);

  // コンテキストメニューのDeleteアクション
  const handleContextMenuDelete = useCallback(() => {
    if (!contextMenu.nodeId) return;

    const node = filteredGraph.nodes.find(n => n.id === contextMenu.nodeId);
    if (!node) return;

    switch (node.type) {
      case 'primary-dataset':
      case 'derived-dataset':
        handleDeleteDataset(node.data as DataSet);
        break;
      case 'report':
        handleDeleteReport(node.data as Report);
        break;
      case 'model':
        handleDeleteModel(node.data as TrainedModel);
        break;
      case 'tab':
        handleCloseTab(node.data as TabState);
        break;
    }
  }, [contextMenu.nodeId, filteredGraph.nodes, handleDeleteDataset, handleDeleteReport, handleDeleteModel, handleCloseTab]);

  // フィルタトグルハンドラ
  const toggleFilter = (filterKey: keyof LineageFilterSettings) => {
    setFilterSettings(prev => ({
      ...prev,
      [filterKey]: !prev[filterKey]
    }));
  };

  // 操作の詳細を表示するヘルパー関数
  const renderOperationDetails = (operation: DerivedDataSet['operation']) => {
    switch (operation.type) {
      case 'sql_query': {
        const sqlOp = operation as SQLQueryOperation;
        const formattedQuery = formatSQL(sqlOp.query, {
          language: 'sql',
          tabWidth: 2,
          keywordCase: 'upper',
        });
        return (
          <div className={styles.operationDetails}>
            <div className={styles.detailsRow}>
              <span className={styles.detailsLabel}>SQL Query:</span>
            </div>
            <pre className={styles.codeBlock}>{formattedQuery}</pre>
          </div>
        );
      }
      case 'crosstab': {
        const ctOp = operation as CrosstabOperation;
        return (
          <div className={styles.operationDetails}>
            <div className={styles.detailsRow}>
              <span className={styles.detailsLabel}>Row Fields:</span> {ctOp.rowFields.join(', ')}
            </div>
            <div className={styles.detailsRow}>
              <span className={styles.detailsLabel}>Column Fields:</span> {ctOp.columnFields.join(', ')}
            </div>
            <div className={styles.detailsRow}>
              <span className={styles.detailsLabel}>Value Fields:</span> {ctOp.valueFields.map(vf => `${vf.aggregation}(${vf.field})`).join(', ')}
            </div>
          </div>
        );
      }
      case 'filter': {
        const filterOp = operation as FilterOperation;
        return (
          <div className={styles.operationDetails}>
            <div className={styles.detailsRow}>
              <span className={styles.detailsLabel}>Filter:</span> {JSON.stringify(filterOp.filterExpression)}
            </div>
          </div>
        );
      }
      default:
        return null;
    }
  };
  
  return (
    <div
      className={styles.container}
    >
      {/* 上部フィルタバー */}
      <div className={styles.filterBar}>
        <div className={styles.filterSection}>
          <span className={styles.filterLabel}>Filters:</span>
          
          <div className={styles.filterItems}>
            <label className={styles.filterItem}>
              <input
                type="checkbox"
                checked={filterSettings.showPrimaryDatasets}
                onChange={() => toggleFilter('showPrimaryDatasets')}
                className={styles.filterCheckbox}
              />
              <span className={styles.filterText}>Primary</span>
            </label>
            
            <label className={styles.filterItem}>
              <input
                type="checkbox"
                checked={filterSettings.showDerivedDatasets}
                onChange={() => toggleFilter('showDerivedDatasets')}
                className={styles.filterCheckbox}
              />
              <span className={styles.filterText}>Derived</span>
            </label>
            
            <label className={styles.filterItem}>
              <input
                type="checkbox"
                checked={filterSettings.showReports}
                onChange={() => toggleFilter('showReports')}
                className={styles.filterCheckbox}
              />
              <span className={styles.filterText}>Reports</span>
            </label>
            
            <label className={styles.filterItem}>
              <input
                type="checkbox"
                checked={filterSettings.showTabs}
                onChange={() => toggleFilter('showTabs')}
                className={styles.filterCheckbox}
              />
              <span className={styles.filterText}>Tabs</span>
            </label>
            
            <label className={styles.filterItem}>
              <input
                type="checkbox"
                checked={filterSettings.showModels}
                onChange={() => toggleFilter('showModels')}
                className={styles.filterCheckbox}
              />
              <span className={styles.filterText}>Models</span>
            </label>
          </div>
        </div>
      </div>
      
      {/* メインコンテンツ */}
      <div className={styles.mainContent}>
        {/* グラフビュー */}
        <div ref={graphViewRef} className={styles.graphView}>
          <LineageGraph
            graph={filteredGraph}
            selectionState={selectionState}
            onNodeSelect={handleNodeSelect}
            onNodeDoubleClick={handleNodeDoubleClick}
            onNodeContextMenu={handleNodeContextMenu}
            onSelectionStateChange={setSelectionState}
          />
        </div>

        {/* Resizer */}
        {selectedNodeDetails && (
          <Resizer
            leftRef={graphViewRef}
            rightRef={detailsPanelRef}
            minLeftWidth={400}
            minRightWidth={250}
          />
        )}

        {/* 詳細パネル */}
        {selectedNodeDetails && (
          <div ref={detailsPanelRef} className={styles.detailsPanel}>
            <div className={styles.detailsHeader}>
              <h3 className={styles.detailsTitle}>
                Details: {selectedNodeDetails.label}
              </h3>
              <button
                className={styles.closeButton}
                onClick={() => handleNodeSelect(null)}
                title="Close details panel"
              >
                ×
              </button>
            </div>
            
            <div className={styles.detailsContent}>
              {/* Primary Dataset */}
              {selectedNodeDetails.type === 'primary-dataset' && (() => {
                const dataset = selectedNodeDetails.data as PrimaryDataSet;
                return (
                  <>
                    <div className={styles.detailsSection}>
                      <div className={styles.sectionTitle}>Basic Information</div>
                      <div className={styles.detailsRow}>
                        <span className={styles.detailsLabel}>Rows:</span> {dataset.rowCount.toLocaleString()}
                      </div>
                      <div className={styles.detailsRow}>
                        <span className={styles.detailsLabel}>Columns:</span> {Object.keys(dataset.schema.columns).length}
                      </div>
                    </div>
                    <div className={styles.detailsSection}>
                      <div className={styles.sectionTitle}>Metadata</div>
                      <div className={styles.detailsRow}>
                        <span className={styles.detailsLabel}>Source File:</span> {dataset.metadata.originalFileName}
                      </div>
                      <div className={styles.detailsRow}>
                        <span className={styles.detailsLabel}>Imported:</span> {new Date(dataset.metadata.importedAt).toLocaleString()}
                      </div>
                      {dataset.metadata.fileSize && (
                        <div className={styles.detailsRow}>
                          <span className={styles.detailsLabel}>File Size:</span> {(dataset.metadata.fileSize / 1024).toFixed(1)} KB
                        </div>
                      )}
                      {dataset.metadata.description && (
                        <div className={styles.detailsRow}>
                          <span className={styles.detailsLabel}>Description:</span> {dataset.metadata.description}
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}

              {/* Derived Dataset */}
              {selectedNodeDetails.type === 'derived-dataset' && (() => {
                const dataset = selectedNodeDetails.data as DerivedDataSet;
                return (
                  <>
                    <div className={styles.detailsSection}>
                      <div className={styles.sectionTitle}>Basic Information</div>
                      <div className={styles.detailsRow}>
                        <span className={styles.detailsLabel}>Rows:</span> {dataset.rowCount?.toLocaleString() || 'N/A'}
                      </div>
                      <div className={styles.detailsRow}>
                        <span className={styles.detailsLabel}>Columns:</span> {Object.keys(dataset.schema.columns).length}
                      </div>
                      <div className={styles.detailsRow}>
                        <span className={styles.detailsLabel}>Created:</span> {new Date(dataset.metadata.createdAt).toLocaleString()}
                      </div>
                    </div>
                    {dataset.metadata.description && (
                      <div className={styles.detailsSection}>
                        <div className={styles.sectionTitle}>Metadata</div>
                        <div className={styles.detailsRow}>
                          <span className={styles.detailsLabel}>Description:</span> {dataset.metadata.description}
                        </div>
                      </div>
                    )}
                    <div className={styles.detailsSection}>
                      <div className={styles.sectionTitle}>Operation: {dataset.operation.type.replace('_', ' ')}</div>
                      {renderOperationDetails(dataset.operation)}
                    </div>
                  </>
                );
              })()}

              {/* Report */}
              {selectedNodeDetails.type === 'report' && (() => {
                const report = selectedNodeDetails.data as Report;
                return (
                  <div className={styles.detailsSection}>
                    <div className={styles.sectionTitle}>Report Information</div>
                    <div className={styles.detailsRow}>
                      <span className={styles.detailsLabel}>Elements:</span> {Object.keys(report.elements || {}).length}
                    </div>
                  </div>
                );
              })()}

              {/* Model */}
              {selectedNodeDetails.type === 'model' && (() => {
                const model = selectedNodeDetails.data as TrainedModel;
                const trainingDataset = model.metadata?.trainingDatasetId
                  ? Object.values(project?.datasets || {}).find(ds => ds.id === model.metadata.trainingDatasetId)
                  : null;
                const trainingDatasetName = trainingDataset?.name || model.metadata?.trainingDatasetId;

                return (
                  <div className={styles.detailsSection}>
                    <div className={styles.sectionTitle}>Model Information</div>
                    <div className={styles.detailsRow}>
                      <span className={styles.detailsLabel}>Type:</span> {model.type || 'Unknown'}
                    </div>
                    {trainingDatasetName && (
                      <div className={styles.detailsRow}>
                        <span className={styles.detailsLabel}>Training Dataset:</span> {trainingDatasetName}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        )}
      </div>

      {/* コンテキストメニュー */}
      <LineageContextMenu
        visible={contextMenu.visible}
        x={contextMenu.x}
        y={contextMenu.y}
        nodeType={contextMenu.nodeType}
        onClose={handleContextMenuClose}
        onOpen={handleContextMenuOpen}
        onDelete={handleContextMenuDelete}
      />
    </div>
  );
};